import { AdminService } from '@admin/services/admin.service';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { CbreAlertService } from '@cbre/alerts';
import { InputProps } from '@shared/components/filters-bar/input-props';
import { TablePaginatorComponent } from '@shared/components/table-paginator/table-paginator.component';
import { HelpTicket, TicketFilter } from '@shared/models/help-ticket.model';
import { ICatalog, PlainOption } from '@shared/models/icommons.model';
import { PaginatedList } from '@shared/models/paginated-list.model';
import { SortEvent } from '@shared/models/sort-event';
import { PRIORITY_FIELD } from '@shared/models/strings.constants';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { DataService } from '@shared/services/data.service';
import { OpenHelpDialogService } from '@shared/services/open-help-dialog.service';
import { SharedService } from '@shared/services/shared.service';
import { TicketServiceService } from '@shared/services/ticket-service.service';
import { L10nTranslationService } from 'angular-l10n';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, merge, Observable, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap, take } from 'rxjs/operators';
import { DEFAULT_TICKET_FILTERS_CONFIG, FILTERS, FILTER_FIELDS } from '../service-tickets.data';
import { TicketMenuComponent } from '../ticket-menu/ticket-menu.component';
import { FEATURE_TOGGLER, FeatureToggler } from '@cbre/feature-toggles';

export interface DeleteEvent {
  index: number;
  ticketId: number;
}
@UntilDestroy()
@Component({
  selector: 'miq-ticket-list',
  templateUrl: './ticket-list.component.html',
  styleUrls: ['./ticket-list.component.scss'],
  standalone: false,
})
export class TicketListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  public isProfile: boolean;
  @Output()
  public totalCount: EventEmitter<number>;
  @Output()
  public ticketSelectedEvent = new EventEmitter<number>();

  public selectedFilters: TicketFilter;
  public isBeingDragged = false;
  public pageSize = 20;
  public isLoadingResults = true;
  public ticketsList: HelpTicket[][];
  public createdByUsers: PlainOption[];
  public assignedToUsers: PlainOption[];
  public totalTickets: number;
  public totalRows: number;
  public sortEvent: SortEvent;
  public isExporting = signal(false);

  public displayedColumns = [
    { field: '', header: '' }, // checkbox.
    { field: 'id', header: 'shared.ticketID' },
    { field: 'jiraNumber', header: 'shared.jira' },
    { field: 'title', header: 'shared.issueTitle' },
    { field: '', header: '' }, // options.
    { field: 'status', header: 'shared.status' },
    { field: 'createBy', header: 'shared.createdBy' },
    { field: 'createTs', header: 'shared.daysOpen' },
    { field: 'assignedTo', header: 'shared.assignedTo' },
    { field: 'updateTs', header: 'shared.dateUpdated' },
    { field: 'countryName', header: 'admin.country' },
    { field: 'lob', header: 'admin.lob' },
    { field: 'hasAttachments', header: 'shared.attachments' },
    { field: PRIORITY_FIELD, header: 'shared.priority' },
  ];

  public filters: InputProps[] = [];
  public statusOptions: ICatalog[] = [];
  countriesList = [];
  statusList = [];
  createdUsersList = [];
  assignedUsersList = [];

  @ViewChild('paginator', { static: true }) paginator: TablePaginatorComponent;
  @ViewChild('menu', { static: true })
  private menu: TicketMenuComponent;
  private userId: number;
  private checkedTickets: any = {};
  private params: any = {};

  $showDownloadBtn = signal(false);

  constructor(
    private shared: SharedService,
    private ticketService: TicketServiceService,
    private cbreAlertService: CbreAlertService,
    private openHelp: OpenHelpDialogService,
    private data: DataService,
    public translationService: L10nTranslationService,
    public adminService: AdminService,
    userInfoResolver: UserInfoResolver,
    @Inject(FEATURE_TOGGLER) private featureToggler: FeatureToggler
  ) {
    this.userId = userInfoResolver.resolve().id;
    this.totalCount = new EventEmitter();
    this.data.getMenuActionCompleted().subscribe(() => (this.checkedTickets = {}));
    this.selectedFilters = this.ticketService.getServiceTicketsFilters();
    this.setFilters();
    this.sortEvent = {
      sortBy: this.isProfile ? 'createTs' : PRIORITY_FIELD,
      sortOrder: -1,
      onSort: new EventEmitter<any>(),
    };
  }

  public ngOnInit() {
    this.removeProfileColumns();
    this.$showDownloadBtn.set(!!this.featureToggler?.isFeatureEnabled?.('manage-service-tickets'));
  }

  public ngAfterViewInit() {
    this.subscribeToTickets();
  }

  ngOnDestroy() {
    /** label translation purpose added */
    this.menu = null;
    this.paginator = null;
  }

  public triggerSortEvent(sortByField: string) {
    const sortBy = this.isProfile ? 'createTs' : PRIORITY_FIELD;
    sortByField = sortByField === 'lob' ? `${sortByField}Desc` : sortByField;
    this.sortEvent.sortOrder = this.getSortOrder(sortByField);

    this.sortEvent.sortBy = this.sortEvent.sortBy !== '' ? sortByField : sortBy;
    this.sortEvent.onSort.emit();
  }

  public deleteTicket(deleteEvent: DeleteEvent) {
    const { index, ticketId } = deleteEvent;
    const deleted = this.ticketsList.splice(index, 1)[0];
    this.shared
      .deleteTicket(ticketId)
      .pipe(take(1))
      .subscribe(
        () => this.sortEvent.onSort.emit(),
        () => {
          this.ticketsList.splice(index, 0, deleted);
          this.cbreAlertService.showInfoSnackBar_l10n('shared.errorDeletingTicket');
        }
      );
  }

  public dragTo(ticketId: number) {
    const flatTickets = this.flatTickets(this.ticketsList);
    let ticketToMove = flatTickets.find(ticket => ticket.id === ticketId);
    const prevPriority = ticketToMove.priority;
    const sortedPriorities = flatTickets.map(ticket => ticket.priority).sort((a, b) => b - a);

    this.ticketsList = this.reassignPriorities(this.ticketsList, sortedPriorities);
    ticketToMove = this.flatTickets(this.ticketsList).find(ticket => ticket.id === ticketId);
    this.shared
      .putMoveTicket(ticketId, { prevPosition: prevPriority, newPosition: ticketToMove.priority })
      .pipe(take(1))
      .subscribe();
  }

  public getHelp() {
    this.openHelp.getCanvas();
  }

  getTicketsFileName = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const yyyy = String(now.getFullYear());
    return `MIQ_service_tickets_${mm}${dd}${yyyy}.csv`;
  };

  public downloadCsv() {
    if (this.isExporting()) return;
    this.isExporting.set(true);

    let selectedFilter = this.assignValue('date', { ...this.selectedFilters });
    selectedFilter = this.assignValue('hasAttachments', selectedFilter);

    this.shared
      .exportTicketsAsCsv(selectedFilter)
      .pipe(take(1))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = this.getTicketsFileName();
          // append to DOM before clicking to ensure compatibility with browsers like Firefox
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.isExporting.set(false);
          this.cbreAlertService.showInfoSnackBar_l10n('shared.errorDownloadingTickets');
        },
        complete: () => {
          this.isExporting.set(false);
        },
      });
  }

  public addChecked(ticketIndex: number) {
    const checkedGroup = this.ticketsList[ticketIndex];
    this.checkedTickets[checkedGroup[0].priority] = checkedGroup;
  }

  public removeUnchecked(priority: number) {
    delete this.checkedTickets[priority];
  }

  public onMenuClick(menuEvent: { ticket: HelpTicket; mouseEvent: MouseEvent }) {
    const { ticket, mouseEvent } = menuEvent;
    this.menu.open(
      this.ticketsList.find(group => group.includes(ticket)),
      mouseEvent,
      this.checkedTickets,
      true
    );
  }

  public onFilterChange(filterEvent: TicketFilter) {
    if (filterEvent.country) {
      this.filters.forEach(filter => {
        if (filter.field === 'lob') {
          filter.options = this.assignLobs(filterEvent.country);
        }
      });
    }
    this.selectedFilters = { ...filterEvent };
    Object.keys(this.selectedFilters).forEach(key => this.assignDefaultValue(key));
    this.subscribeToTickets();
  }

  public assignDefaultValue(field: string) {
    this.selectedFilters[field] =
      this.selectedFilters[field] !== '' ? this.selectedFilters[field] : DEFAULT_TICKET_FILTERS_CONFIG[field];
  }

  public clearFilters() {
    this.onFilterChange(this.ticketService.clearServiceTicketsFilters());
    this.assignFilters();
  }

  private setFilters() {
    const createdUsers = this.shared.getTicketFilterUsers({ query: 'createdBy' });
    const assignedToUsers = this.shared.getTicketFilterUsers({ query: 'assignedTo' });
    const status = this.shared.getServiceTicketStatuses();
    const countries: Observable<[]> = this.adminService.getCountryLobData() as Observable<[]>;
    forkJoin(status, createdUsers, assignedToUsers, countries)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        [this.statusList, this.createdUsersList, this.assignedUsersList, this.countriesList] = response;
        this.assignFilters();
      });
  }

  private assignFilters() {
    this.filters = FILTERS.filter(filter => {
      const fil = this.isProfile ? FILTER_FIELDS.profile : FILTER_FIELDS.admin;
      return fil.indexOf(filter.field) >= 0 ? filter : null;
    }).map(filter => {
      switch (filter.field) {
        case 'statusId':
          filter.options = this.statusList;
          break;
        case 'createdBy':
          filter.options = this.createdUsersList;
          break;
        case 'assignedTo':
          filter.options = this.assignedUsersList;
          break;
        case 'country':
          filter.options = this.countriesList.map(country => {
            country.value = country.countryName;
            return { ...country };
          });
          break;
        case 'lob':
          if (this.selectedFilters.country && this.selectedFilters.country.length) {
            filter.options = this.assignLobs(this.selectedFilters.country);
          } else {
            filter.options = [];
          }
          break;
        case 'searchText':
          if (filter.placeholder) {
            filter.placeholder = filter.placeholder.includes('.')
              ? this.translationService.translate(filter.placeholder)
              : filter.placeholder;
          }
          break;
        default:
          if (filter.options && filter.options.length > 0) {
            filter.options.forEach(opt => {
              opt.value =
                opt.value && opt.value.includes('.') ? this.translationService.translate(opt.value) : opt.value;
            });
          }
      }
      filter.value = this.selectedFilters[filter.field];
      return { ...filter };
    });
  }

  private assignLobs(countryKeys: number[]) {
    let options = [];
    this.countriesList.forEach(country => {
      if (countryKeys.indexOf(country.key as never) >= 0) {
        let lobs = [];
        lobs = country.lobs.map(lob => {
          lob.key = lob.lobId;
          lob.value = lob.lobName;
          return { ...lob };
        });
        options = options.concat(lobs);
      }
    });
    return options;
  }

  private getTickets(): Observable<PaginatedList<HelpTicket[]>> {
    /*
     * add sortBy and sortOrder to getTickets method:
     * this.sort.sortBy;
     * this.sort.sortOrder;
     */
    const { sortBy, sortOrder } = this.sortEvent;
    const pageSize = this.paginator.pageSize || this.pageSize;
    this.params = {
      offset: (this.paginator.pageIndex * pageSize).toString(),
      limit: pageSize.toString(),
      sortField: sortBy,
      sortOrder: sortOrder > 0 ? 'ASC' : 'DESC',
      userId: this.isProfile ? this.userId.toString() : null,
    };
    this.ticketService.setServiceTicketsFilters(this.selectedFilters);
    /* As the date field actual value is object and we can not assign to select option
    and need to send the actuaal value to service */
    let selectedFilter = this.assignValue('date', { ...this.selectedFilters });
    selectedFilter = this.assignValue('hasAttachments', selectedFilter);
    return this.shared.serviceTicketsFiltering(selectedFilter, this.params);
  }

  private assignValue(key: string, selectedFilter: TicketFilter) {
    const foundField = this.filters.length
      ? this.filters.find(filter => filter.field === key)
      : FILTERS.find(filter => filter.field === key);
    if (foundField && selectedFilter[key]) {
      const field = foundField.options.find(option => option.key == selectedFilter[key]);
      selectedFilter[key] = field ? field.actualValue : selectedFilter[key];
    }
    return selectedFilter;
  }

  private subscribeToTickets() {
    merge(
      this.paginator.pageChange,
      this.sortEvent.onSort,
      this.data.getMenuActionCompleted(),
      this.data.getHelpClosed()
    )
      .pipe(
        startWith({ elements: [] }),
        switchMap(this.getTickets.bind(this)),
        map(this.mapTicketsResponse.bind(this)),
        catchError(() => {
          return observableOf([]);
        })
      )
      .subscribe((tickets: HelpTicket[][]) => {
        this.ticketsList = tickets;
      });
  }

  private mapTicketsResponse(res: PaginatedList<HelpTicket[]>): HelpTicket[][] {
    // totalCount: total tickets
    // totalRows: for pagination
    this.isLoadingResults = false;
    this.totalTickets = !!res.totalCount ? res.totalCount : 0;
    this.totalRows = res.totalRows ?? 0;
    this.totalCount.emit(this.totalTickets);
    return this.mapTicketsChecks(res.elements, this.checkedTickets);
  }

  private removeProfileColumns() {
    if (this.isProfile) {
      this.displayedColumns = this.displayedColumns
        .filter(col => col.field !== '')
        .filter(col => col.field !== PRIORITY_FIELD)
        .filter(col => col.field !== 'createBy');
    }
    this.displayedColumns.push({ field: '', header: '' }); // details.
  }

  /**
   * Flat ticket list and remove elements from groups
   * for easy handling.
   *
   * @param ticketList
   */
  private flatTickets(ticketList: HelpTicket[][]) {
    return ticketList.reduce((acc, val) => [...acc, val[0]], []);
  }

  /**
   * Reassign sorted priorities to all the tickets of a list.
   *
   * @param ticketList
   * @param sortedPriorities
   */
  private reassignPriorities(ticketList: HelpTicket[][], sortedPriorities: number[]): HelpTicket[][] {
    return ticketList.map((group, index) => group.map(ticket => ({ ...ticket, priority: sortedPriorities[index] })));
  }

  private mapTicketsChecks(ticketList: HelpTicket[][], checkedTickets: any): HelpTicket[][] {
    return ticketList.map(group => group.map(ticket => ({ ...ticket, checked: !!checkedTickets[ticket.priority] })));
  }

  private getSortOrder(sortByField: string) {
    return this.sortEvent.sortBy === sortByField && this.sortEvent.sortOrder === -1 && sortByField !== PRIORITY_FIELD
      ? 1
      : -1;
  }

  showTicketDetail(ticketDetailEvent) {
    this.ticketSelectedEvent.emit(ticketDetailEvent);
  }
}
